import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Stack,
  Text,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomDatePicker from '../../../components/CustomDatePicker';
import { getAppSettings, saveEventDatesAdmin } from '../../../api';
import { ModalInfo } from '../../../components/ModalInfo';

export default function EventDatesEditor() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [formIndex, setFormIndex] = useState(null);
  const [formDay, setFormDay] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [formTurn, setFormTurn] = useState('lunch'); // 'lunch' | 'dinner' | 'both'

  const sortRowsByDate = (rowsToSort) =>
    rowsToSort
      .slice()
      .sort(
        (a, b) =>
          (a.day instanceof Date ? a.day.getTime() : new Date(a.day).getTime()) -
          (b.day instanceof Date ? b.day.getTime() : new Date(b.day).getTime()),
      );

  const isLunchDate = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return d.getHours() < 17;
  };

  const getDayKey = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const load = async () => {
    try {
      setIsLoading(true);
      const settings = await getAppSettings();
      const eventDates = Array.isArray(settings?.eventDates) ? settings.eventDates : [];

      const byDay = new Map();

      eventDates.forEach((e) => {
        const date = e.date instanceof Date ? e.date : new Date(e.date);
        const key = getDayKey(date);
        const existing = byDay.get(key);
        const day = new Date(date);
        day.setHours(0, 0, 0, 0);
        const isLunch = isLunchDate(date);

        if (!existing) {
          byDay.set(key, {
            day,
            hasLunch: isLunch,
            hasDinner: !isLunch,
            status: e.status || 'open',
          });
        } else {
          if (isLunch) existing.hasLunch = true;
          else existing.hasDinner = true;
        }
      });

      const mapped = Array.from(byDay.values());
      setRows(sortRowsByDate(mapped));
      setError('');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Errore nel caricamento delle date');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canSave = useMemo(() => {
    const validDates = rows.every((r) => r.day instanceof Date && !Number.isNaN(r.day.getTime()));
    if (!validDates) return false;
    const times = rows.flatMap((r) => {
      const base = new Date(r.day);
      base.setHours(0, 0, 0, 0);
      const result = [];
      if (r.hasLunch) {
        const d = new Date(base);
        d.setHours(13, 0, 0, 0);
        result.push(d.getTime());
      }
      if (r.hasDinner) {
        const d = new Date(base);
        d.setHours(20, 0, 0, 0);
        result.push(d.getTime());
      }
      return result;
    });
    const uniq = new Set(times);
    return uniq.size === times.length && times.length > 0;
  }, [rows]);

  const addRow = () => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    setRows((prev) =>
      sortRowsByDate([
        ...prev,
        {
          day: base,
          hasLunch: true,
          hasDinner: false,
          status: 'open',
        },
      ]),
    );
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const confirmRemoveRow = () => {
    if (rowToDelete === null) return;
    removeRow(rowToDelete);
    setRowToDelete(null);
  };

  const cancelRemoveRow = () => {
    setRowToDelete(null);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => sortRowsByDate(prev.map((r, i) => (i === idx ? { ...r, ...patch } : r))));
  };

  const getRowTurnValue = (row) => {
    if (row.hasLunch && row.hasDinner) return 'both';
    if (row.hasLunch) return 'lunch';
    return 'dinner';
  };

  const updateRowDateOnly = (idx, newDate) => {
    if (!newDate) return;
    setRows((prev) =>
      sortRowsByDate(
        prev.map((r, i) => {
          if (i !== idx) return r;
          const base = new Date(newDate);
          base.setHours(0, 0, 0, 0);
          return { ...r, day: base };
        }),
      ),
    );
  };

  const updateRowTurn = (idx, newTurn) => {
    setRows((prev) =>
      sortRowsByDate(
        prev.map((r, i) => {
          if (i !== idx) return r;
          if (newTurn === 'both') {
            return { ...r, hasLunch: true, hasDinner: true };
          }
          if (newTurn === 'lunch') {
            return { ...r, hasLunch: true, hasDinner: false };
          }
          return { ...r, hasLunch: false, hasDinner: true };
        }),
      ),
    );
  };

  const openCreateForm = () => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    setFormDay(base);
    setFormTurn('lunch');
    setFormMode('create');
    setFormIndex(null);
    setIsFormOpen(true);
  };

  const openEditForm = (idx) => {
    const row = rows[idx];
    const base = new Date(row.day);
    base.setHours(0, 0, 0, 0);
    setFormDay(base);
    setFormTurn(getRowTurnValue(row));
    setFormMode('edit');
    setFormIndex(idx);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setFormIndex(null);
  };

  const submitForm = () => {
    const base = new Date(formDay);
    base.setHours(0, 0, 0, 0);
    const newData = {
      day: base,
      hasLunch: formTurn === 'lunch' || formTurn === 'both',
      hasDinner: formTurn === 'dinner' || formTurn === 'both',
      status: 'open',
    };

    if (formMode === 'create') {
      setRows((prev) => sortRowsByDate([...prev, newData]));
    } else if (formMode === 'edit' && formIndex !== null) {
      setRows((prev) =>
        sortRowsByDate(prev.map((r, i) => (i === formIndex ? { ...r, ...newData } : r))),
      );
    }

    closeForm();
  };

  const save = async () => {
    try {
      setIsSaving(true);
      setSuccess('');
      setError('');

      if (!canSave) {
        throw new Error('Date non valide (duplicati o formato errato)');
      }

      const expandedDates = rows.flatMap((r) => {
        const base = new Date(r.day);
        base.setHours(0, 0, 0, 0);
        const result = [];
        if (r.hasLunch) {
          const d = new Date(base);
          d.setHours(13, 0, 0, 0);
          result.push({ date: d, status: r.status });
        }
        if (r.hasDinner) {
          const d = new Date(base);
          d.setHours(20, 0, 0, 0);
          result.push({ date: d, status: r.status });
        }
        return result;
      });

      const payload = expandedDates
        .slice()
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map((r) => ({
          date: r.date.toISOString(),
          status: r.status,
        }));

      const res = await saveEventDatesAdmin(payload);
      setSuccess(`Salvato. Prodotti aggiornati: ${res.result?.updated ?? '?'}`);
      await load();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VStack w='100%' align='stretch'>
      <Heading size='md' mb={2} color='white'>
        Date evento
      </Heading>
      {/* <Text color='white' mb={3}>
        Salvando le date, verranno ricalcolate automaticamente le <b>quantities</b> di tutti i prodotti.
      </Text> */}

      {error && (
        <Alert status='error' mb={4}>
          <AlertIcon />
          <AlertTitle mr={2}>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert status='success' mb={4}>
          <AlertIcon />
          <AlertTitle mr={2}>OK</AlertTitle>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Stack direction='row' justify='flex-end' mt={2} mb={2}>
        <Button onClick={openCreateForm} isDisabled={isLoading}>
          Aggiungi data
        </Button>
      </Stack>

      <Stack spacing={3}>
        {rows.map((r, idx) => (
          <Flex
            key={`${r.date?.toString?.() || 'date'}-${idx}`}
            p={3}
            border='1px solid grey'
            borderRadius={12}
            align='center'
            gap={3}
            wrap='wrap'
          >
            <Box>
              <Text color='white' fontWeight={600} mb={1}>
                Data
              </Text>
              <Text color='white'>
                {(r.day instanceof Date ? r.day : new Date(r.day)).toLocaleDateString('it-IT')}
              </Text>
            </Box>
            <Box>
              <Text color='white' fontWeight={600} mb={1}>
                Turno
              </Text>
              <Text color='white'>
                {getRowTurnValue(r) === 'both'
                  ? 'Pranzo e Cena'
                  : getRowTurnValue(r) === 'lunch'
                  ? 'Pranzo'
                  : 'Cena'}
              </Text>
            </Box>
            <Box flex='1' />
            <Stack direction='row' spacing={2}>
              <Button size='sm' onClick={() => openEditForm(idx)}>
                Modifica
              </Button>
              <Button size='sm' onClick={() => setRowToDelete(idx)}>
                Rimuovi
              </Button>
            </Stack>
          </Flex>
        ))}
      </Stack>

      <Stack direction='row' justify='space-between' mt={4}>
        <Box />
        <Button onClick={save} isLoading={isSaving} isDisabled={!canSave || isLoading}>
          Salva
        </Button>
      </Stack>

      <ModalInfo
        title='Conferma rimozione data'
        body='Sei sicuro di voler rimuovere questa data evento?'
        isOpen={rowToDelete !== null}
        onClose={cancelRemoveRow}
        onCloseText='Annulla'
        onCancel={confirmRemoveRow}
        onCancelText='Rimuovi'
      />

      <Modal isOpen={isFormOpen} onClose={closeForm} blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === 'create' ? 'Aggiungi data' : 'Modifica data'}</ModalHeader>
          <ModalBody>
            <VStack align='stretch' spacing={4}>
              <Box>
                <Text fontWeight={600} mb={1}>
                  Data
                </Text>
                <DatePicker
                  dateFormat='dd/MM/yyyy'
                  selected={formDay}
                  onChange={(d) => {
                    if (!d) return;
                    const base = new Date(d);
                    base.setHours(0, 0, 0, 0);
                    setFormDay(base);
                  }}
                  customInput={<CustomDatePicker />}
                />
              </Box>
              <Box>
                <Text fontWeight={600} mb={1}>
                  Turno
                </Text>
                <Select value={formTurn} onChange={(e) => setFormTurn(e.target.value)} w='200px'>
                  <option value='lunch'>Pranzo</option>
                  <option value='dinner'>Cena</option>
                  <option value='both'>Pranzo e Cena</option>
                </Select>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction='row' spacing={3}>
              <Button variant='ghost' onClick={closeForm}>
                Annulla
              </Button>
              <Button onClick={submitForm} isDisabled={!formDay}>
                Conferma
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

